import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { SmartLink, formatBalanceToAbel, pruneCenter, toUTCDateTime } from '../../../abelcommon';
import { CenteredTableCell } from '../../../abelcommon/components/CenteredTableCell';
import { selectedMiningAccountState } from '../../../api';
import { ABELSCAN_TX_URL } from '../../../api/abel';
import { PayoutHistoryStatus, loadBalancerAPI } from '../../../api/load-balancer';
import { MountableLoadingOverlay } from '../../../abelcommon/components/LoadingOverlay';
import { useTranslation } from 'react-i18next';

const transactionStylesMap = {
  [PayoutHistoryStatus.CONFIRMED]: { color: 'success.main', content: 'Paid' },
  [PayoutHistoryStatus.SUCCESS]: { color: 'success.main', content: 'Paid' },
  [PayoutHistoryStatus.PENDING]: { color: 'warning.main', content: 'Pending' },
  [PayoutHistoryStatus.FAILED]: { color: 'error.main', content: 'Failed' },
};

export const PayoutHistorySection = () => {
  const [requestId, setRequestId] = useState<string>(undefined);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const selectedMiningAccount = useRecoilValue(selectedMiningAccountState);
  const { t } = useTranslation();
  const {
    data: payoutHistory,
    isLoading,
    error,
    isPreviousData,
  } = loadBalancerAPI.hookWrapperQuery.useGetPayoutHistory(
    selectedMiningAccount.id,
    rowsPerPage,
    page * rowsPerPage,
    undefined,
    requestId
  );
  const dateFormat = 'yyyy-MM-dd HH:mm:ss';

  const handleChangeRowsPerPage = (nextRowsPerPage: number) => {
    setPage(0);
    setRowsPerPage(nextRowsPerPage);
    setRequestId(new Date().getTime().toString());
  };

  return (
    <Card>
      <CardHeader
        title={t('payoutHistorySectionPage.payout')}
        sx={{ flexWrap: 'wrap' }}
        action={
          <Stack
            sx={{ flexDirection: 'row', alignItems: 'center', gap: 1, color: 'appGray.contrastText', fontWeight: 400 }}
          >
            <Typography sx={{ color: 'success.main', fontWeight: 500 }}>
              "{t('payoutHistorySectionPage.paid')}"
            </Typography>
            {t('payoutHistorySectionPage.meansTheABELIsSentToYourWallet')}
          </Stack>
        }
      />
      <CardContent sx={{ overflow: 'auto', position: 'relative' }}>
        <TableContainer sx={{ minHeight: '100px', overflow: 'auto' }}>
          <Table sx={{ minWidth: '500px' }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('payoutHistorySectionPage.payoutDate')} (UTC)</TableCell>
                <TableCell>{t('payoutHistorySectionPage.blockHeight')}</TableCell>
                <TableCell>ABEL</TableCell>
                <TableCell>{t('payoutHistorySectionPage.status')}</TableCell>
                <TableCell>{t('payoutHistorySectionPage.transactionHash')}</TableCell>
                <TableCell>{t('payoutHistorySectionPage.walletAddress')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <CenteredTableCell colSpan={7}>
                    <CircularProgress />
                  </CenteredTableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <CenteredTableCell colSpan={7}>
                    <Alert severity="error" sx={{ backgroundColor: 'transparent', p: 0, textAlign: 'center' }}>
                      {t('payoutHistorySectionPage.cannotGetPayoutHistoryInfo')}
                    </Alert>
                  </CenteredTableCell>
                </TableRow>
              ) : payoutHistory.total === 0 || !payoutHistory.data ? (
                <TableRow>
                  <CenteredTableCell colSpan={7}>{t('global.noData')}</CenteredTableCell>
                </TableRow>
              ) : (
                payoutHistory.data.map((row, index) => (
                  <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {format(toUTCDateTime(row.createdAt), dateFormat)}
                    </TableCell>
                    <TableCell>{row.blockHeight}</TableCell>
                    <TableCell>{formatBalanceToAbel(row.amount, 7)}</TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          textTransform: 'capitalize',
                          color: transactionStylesMap[row.status].color,
                          fontWeight: 500,
                        }}
                      >
                        {t(`payoutHistorySectionPage.${transactionStylesMap[row.status].content.toLowerCase()}`)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <SmartLink to={`${ABELSCAN_TX_URL}/${row.transactionId}`}>
                        {pruneCenter(row.transactionId, 10)}
                      </SmartLink>
                    </TableCell>
                    <TableCell>{pruneCenter(row.toWalletAddress, 10)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          sx={{ pl: 0 }}
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={payoutHistory?.total ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, page) => setPage(page)}
          labelRowsPerPage={t('global.rowsPerPage')}
          onRowsPerPageChange={(e) => handleChangeRowsPerPage(+e.target.value)}
        />
        {isPreviousData && <MountableLoadingOverlay />}
      </CardContent>
    </Card>
  );
};
